import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { useAuth, useTenantBootstrap, useTenantWorkDashboard } from '@tark/shared/hooks';
import type { ActiveTenantSummary, TenantMembershipSummary } from '@tark/shared/types';
import { cn } from '@tark/shared/lib';
import BrandMark from '../components/BrandMark';
import WorkspaceDialog from '../components/WorkspaceDialog';
import {
  COMMUNITY_HOME_ROUTE,
  hasTenantWorkspaceContext,
  isCommunityWelcomeRequest,
} from '../lib/workspaceContext';
import {
  ArrowRightLeft,
  BookOpenCheck,
  Calendar,
  CircleUserRound,
  CreditCard,
  Home,
  Inbox,
  ListChecks,
  Loader2,
  LogOut,
  Medal,
  PanelLeftClose,
  PanelLeftOpen,
  Radio,
  MessageCircleQuestion,
  Settings,
  ShieldCheck,
  Sparkles,
  Target,
  Trophy,
  Users,
  X,
} from 'lucide-react';

const ICON_MAP = {
  home: Home,
  squads: Users,
  play: Medal,
  pros: Sparkles,
  buzz: Radio,
  directory: Users,
  classes: BookOpenCheck,
  schedule: Calendar,
  attendance: ListChecks,
  goals: Target,
  payments: CreditCard,
  requests: Inbox,
  competitions: Trophy,
  access: ShieldCheck,
  settings: Settings,
  me: CircleUserRound,
  report: MessageCircleQuestion,
} as const;

const SIDEBAR_COLLAPSED_STORAGE_KEY = 'tark_web_sidebar_collapsed';

type NavItem = {
  to: string;
  label: string;
  icon: keyof typeof ICON_MAP;
  mobile?: boolean;
  end?: boolean;
};

export default function AppLayout() {
  const { user, logout, isLoggingOut } = useAuth();
  const {
    memberships,
    activeTenant,
    selectTenant,
    isSwitchingTenant,
    isLoading: isWorkspaceLoading,
  } = useTenantBootstrap(Boolean(user));
  const navigate = useNavigate();
  const location = useLocation();
  const isCommunityModeRequested = isCommunityWelcomeRequest(location.pathname, location.search);
  const [showContextSwitcher, setShowContextSwitcher] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    return typeof window !== 'undefined' && window.localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === 'true';
  });

  const isTenantContext = hasTenantWorkspaceContext(activeTenant?.tenant?.id, isCommunityModeRequested);
  const homeRoute = isTenantContext ? '/welcome' : COMMUNITY_HOME_ROUTE;
  const isRedesignedWorkspace = activeTenant?.tenant?.tenantArchetype === 'COACHING' || ['/welcome', '/classes', '/schedule', '/directory', '/attendance', '/payments'].includes(location.pathname) || location.pathname.startsWith('/organizer/') || location.pathname.startsWith('/schedule/roster/') || location.pathname.startsWith('/stringing/');
  const isSquadsSection = location.pathname.startsWith('/squads');
  const workTabLabel = activeTenant?.tenant?.workTabLabel || 'Schedule';
  const directoryTabLabel =
    activeTenant?.tenant?.tenantArchetype === 'COACHING'
      ? 'Students'
      : activeTenant?.tenant?.directoryTabLabel || 'People';
  const activeTenantRole = activeTenant?.roleCode?.toUpperCase() || '';
  const canManageTenant = ['OWNER', 'ADMIN', 'COACH', 'STAFF'].includes(activeTenantRole);
  const isCoachWorkspace = isTenantContext && activeTenant?.tenant?.tenantArchetype === 'COACHING';
  const work = useTenantWorkDashboard(Boolean(user) && isTenantContext && activeTenant?.tenant?.tenantArchetype === 'SERVICE_PROVIDER');
  const isStringingWorkspace = isTenantContext && activeTenant?.tenant?.tenantArchetype === 'SERVICE_PROVIDER' && work.data?.linkedProvider?.providerTypes.includes('STRINGER');
  const isEventOperatorWorkspace =
    isTenantContext &&
    activeTenant?.tenant?.tenantArchetype === 'EVENT_OPERATOR' &&
    activeTenant?.tenant?.workSurface === 'EVENTS';
  const currentContextName = activeTenant?.tenant?.displayName || 'Play';
  const currentContextMeta = activeTenant?.roleCode
    ? `${activeTenant.roleCode} • ${
        isCoachWorkspace ? 'Coach Studio' : isEventOperatorWorkspace ? 'Organizer Studio' : workTabLabel
      }`
    : 'Casual play, squads, and discovery';

  const navItems = useMemo<NavItem[]>(() => {
    if (isTenantContext) {
      if (isStringingWorkspace && canManageTenant) {
        return [
          { to: '/stringing/today', icon: 'home', label: 'Today', mobile: true },
          { to: '/stringing/orders', icon: 'requests', label: 'Orders', mobile: true, end: false },
          { to: '/stringing/clients', icon: 'directory', label: 'Clients', mobile: true, end: false },
          { to: '/stringing/services', icon: 'settings', label: 'Services', mobile: true },
          { to: '/stringing/inquiries', icon: 'requests', label: 'Inquiries' },
          { to: '/tenant-access', icon: 'access', label: 'Access' },
          { to: '/pros/businesses', icon: 'pros', label: 'Manage businesses' },
          { to: '/profile', icon: 'me', label: 'Me', mobile: true },
        ];
      }
      if (isEventOperatorWorkspace) {
        return [
          { to: '/welcome', icon: 'home', label: 'Home', mobile: true },
          { to: '/organizer/competitions', icon: 'competitions', label: 'Tournaments', mobile: true, end: false },
          { to: '/organizer/leagues', icon: 'competitions', label: 'League events', end: false },
          { to: '/directory', icon: 'directory', label: directoryTabLabel, mobile: true },
          { to: '/tenant-access', icon: 'access', label: 'Access', mobile: true },
          { to: '/profile', icon: 'me', label: 'Me', mobile: true },
        ];
      }

      if (isCoachWorkspace) {
        if (!canManageTenant) return [
          {to:'/family',icon:'directory',label:'Family',mobile:true},
          {to:'/pros',icon:'pros',label:'Find a coach',mobile:true},
          {to:'/profile',icon:'me',label:'Me',mobile:true},
        ];
        return [
          { to: '/welcome', icon: 'home', label: 'Home', mobile: true },
          { to: '/classes', icon: 'classes', label: 'Classes', mobile: true },
          { to: '/schedule', icon: 'schedule', label: 'Schedule', mobile: true, end: false },
          {
            to: canManageTenant ? '/directory' : '/family',
            icon: 'directory',
            label: canManageTenant ? 'Students' : 'Family',
            mobile: true,
          },
          { to: '/attendance', icon: 'attendance', label: 'Attendance' },
          { to: '/payments', icon: 'payments', label: 'Payments' },
          { to: '/leads', icon: 'requests', label: 'Leads' },
          { to: '/studio/settings', icon: 'settings', label: 'Settings' },
          {
            to: canManageTenant ? '/tenant-access' : '/directory',
            icon: canManageTenant ? 'access' : 'directory',
            label: canManageTenant ? 'Access' : directoryTabLabel,
            mobile: true,
          },
          { to: '/pros/businesses', icon: 'pros', label: 'Manage businesses' },
          { to: '/profile', icon: 'me', label: 'Me', mobile: true },
        ];
      }

      return [
        { to: '/welcome', icon: 'home', label: 'Home' },
        { to: '/schedule', icon: 'schedule', label: workTabLabel },
        {
          to: canManageTenant ? '/directory' : '/family',
          icon: 'directory',
          label: canManageTenant ? directoryTabLabel : 'Family',
        },
        {
          to: canManageTenant ? '/tenant-access' : '/directory',
          icon: canManageTenant ? 'access' : 'directory',
          label: canManageTenant ? 'Access' : directoryTabLabel,
        },
        { to: '/profile', icon: 'me', label: 'Me' },
      ];
    }

    return [
      { to: homeRoute, icon: 'home', label: 'Home' },
      { to: '/squads', icon: 'squads', label: 'Squads', end: false },
      { to: '/play', icon: 'play', label: 'Matches' },
      { to: '/pros', icon: 'pros', label: 'PROs', end: false },
      { to: '/buzz', icon: 'buzz', label: 'Buzz', end: false },
      { to: '/profile', icon: 'me', label: 'Me' },
    ];
  }, [canManageTenant, directoryTabLabel, homeRoute, isCoachWorkspace, isStringingWorkspace, isEventOperatorWorkspace, isTenantContext, workTabLabel]);

  const mobileNavItems = useMemo(() => {
    const explicit = navItems.filter((item) => item.mobile === true);
    if (explicit.length > 0) {
      return explicit.slice(0, 5);
    }

    return navItems.filter((item) => item.mobile !== false);
  }, [navItems]);

  const isNavItemActive = (item: NavItem) => {
    const [pathAndQuery, hash = ''] = item.to.split('#');
    const [pathname] = pathAndQuery.split('?');
    if (location.pathname !== pathname) {
      return item.end === false && location.pathname.startsWith(`${pathname}/`);
    }

    if (hash) {
      return location.hash === `#${hash}`;
    }

    return item.end === false ? location.pathname.startsWith(pathname) : location.hash === '';
  };

  useEffect(() => {
    try {
      window.localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(isSidebarCollapsed));
    } catch {
      // Ignore storage failures; the sidebar still works for this session.
    }
  }, [isSidebarCollapsed]);

  useEffect(() => {
    if (!isCommunityModeRequested || !activeTenant?.tenant?.id || isSwitchingTenant) {
      return;
    }

    // The explicit Community URL is a context switch, not only a visual mode.
    void selectTenant({ communityMode: true });
  }, [activeTenant?.tenant?.id, isCommunityModeRequested, isSwitchingTenant, selectTenant]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleCommunitySelect = async () => {
    await selectTenant({ communityMode: true });
    setShowContextSwitcher(false);
    navigate('/welcome', { replace: true });
  };

  const handleTenantSelect = async (tenantId: number) => {
    await selectTenant({ tenantId });
    setShowContextSwitcher(false);
    navigate('/welcome', { replace: true });
  };

  return (
    <div className={cn("tk-shell min-h-screen bg-background text-text", isRedesignedWorkspace && "tk-workspace", isCoachWorkspace && "tk-coach-journey", isSidebarCollapsed && "tk-sidebar-collapsed")}>
      <header className="sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur lg:hidden">
        <div className="flex h-16 items-center justify-between px-4">
          <NavLink to={homeRoute} className="flex items-center gap-3">
            <BrandMark
              size="md"
              tone="theme"
              titleClassName="text-base"
            />
          </NavLink>

          <button
            type="button"
            onClick={() => setShowContextSwitcher(true)}
            className="inline-flex shrink-0 items-center gap-2 rounded-full border border-border bg-surface px-3 py-2 text-sm font-medium text-text transition-colors hover:border-primary/30 hover:bg-surface-elevated"
            aria-haspopup="dialog"
            aria-expanded={showContextSwitcher}
            aria-label="Switch view"
            title="Switch view"
          >
            <ArrowRightLeft className="h-4 w-4 shrink-0 text-primary" />
            <span className="hidden sm:inline">Switch</span>
          </button>
        </div>
      </header>

      <aside
        className={cn(
          'fixed inset-y-0 left-0 hidden border-r border-border bg-surface transition-[width] duration-200 lg:flex lg:flex-col',
          'overflow-x-hidden overflow-y-auto tk-sidebar',
          isSidebarCollapsed ? 'w-20' : 'w-72'
        )}
      >
        <div className={cn('flex h-full flex-col py-6', isSidebarCollapsed ? 'px-3' : 'px-5')}>
          <div className={cn('flex items-center', isSidebarCollapsed ? 'justify-center' : 'justify-between gap-3')}>
            <NavLink
              to={homeRoute}
              className={cn('flex min-w-0 items-center gap-3', isSidebarCollapsed ? 'justify-center' : '')}
              aria-label="TARK home"
            >
              <BrandMark
                size={isRedesignedWorkspace ? 'sm' : isSidebarCollapsed ? 'md' : 'lg'}
                tone="theme"
                titleClassName="text-lg"
                showText={!isSidebarCollapsed}
              />
            </NavLink>
            <button
              type="button"
              onClick={() => setIsSidebarCollapsed((current) => !current)}
              className={cn(
                'absolute top-7 z-20 inline-flex h-9 w-9 items-center justify-center rounded-xl border border-border bg-background text-text-muted shadow-md transition-colors hover:border-primary/30 hover:bg-surface-elevated hover:text-text',
                isSidebarCollapsed ? '-right-4' : 'right-4'
              )}
              aria-label={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              title={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {isSidebarCollapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
            </button>
          </div>

          {isTenantContext ? (
            <button
              type="button"
              onClick={() => setShowContextSwitcher(true)}
              className={cn(
                'mt-6 border border-border bg-background text-left transition-all hover:border-primary/30 hover:bg-surface-elevated',
                isSidebarCollapsed ? 'flex h-12 items-center justify-center rounded-2xl p-0' : 'rounded-3xl px-4 py-4'
              )}
              aria-haspopup="dialog"
              aria-expanded={showContextSwitcher}
              aria-label="Switch view"
              title={isSidebarCollapsed ? 'Switch view' : undefined}
            >
              <div className={cn('flex items-start justify-between gap-3', isSidebarCollapsed ? 'hidden' : '')}>
                <div>
                  <p className="text-[11px] uppercase tracking-[0.18em] text-text-subtle">
                    {isCoachWorkspace ? 'Coach Studio' : isEventOperatorWorkspace ? 'Organizer Studio' : 'Active view'}
                  </p>
                  <p className="mt-2 text-lg font-semibold text-text">{currentContextName}</p>
                  <p className="mt-1 text-sm text-text-muted">{currentContextMeta}</p>
                </div>
                <div className="rounded-2xl bg-primary/10 p-3 text-primary">
                  <ArrowRightLeft className="h-5 w-5" />
                </div>
              </div>
              {isSidebarCollapsed ? <ArrowRightLeft className="h-5 w-5 text-primary" /> : null}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setShowContextSwitcher(true)}
              className={cn(
                'mt-6 inline-flex items-center border border-border bg-background text-left transition-all hover:border-primary/30 hover:bg-surface-elevated',
                isSidebarCollapsed
                  ? 'h-12 justify-center rounded-2xl p-0'
                  : 'justify-between gap-3 rounded-2xl px-4 py-3'
              )}
              aria-haspopup="dialog"
              aria-expanded={showContextSwitcher}
              aria-label="Switch view"
              title={isSidebarCollapsed ? 'Switch view' : undefined}
            >
              <span className={cn('text-sm font-medium text-text', isSidebarCollapsed ? 'hidden' : '')}>Switch view</span>
              <div className={cn('rounded-xl bg-primary/10 p-2 text-primary', isSidebarCollapsed ? 'bg-transparent p-0' : '')}>
                <ArrowRightLeft className="h-4 w-4" />
              </div>
            </button>
          )}

          <nav className="mt-8 space-y-1">
            {isCoachWorkspace ? (
              <p className={cn('px-4 pb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-text-subtle', isSidebarCollapsed ? 'sr-only' : '')}>
                Studio
              </p>
            ) : null}
            {navItems.map((item) => {
              const Icon = ICON_MAP[item.icon];
              const isActive = isNavItemActive(item);
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end ?? !item.to.includes('#')}
                  className={() =>
                    cn(
                      'flex items-center rounded-2xl text-sm font-medium transition-all duration-200',
                      isSidebarCollapsed ? 'justify-center px-0 py-3' : 'gap-3 px-4 py-3',
                      isActive
                        ? 'bg-primary [color:var(--color-on-primary)] shadow-lg shadow-primary/20'
                        : 'text-text-muted hover:bg-surface-elevated hover:text-text'
                    )
                  }
                  aria-label={item.label}
                  title={isSidebarCollapsed ? item.label : undefined}
                >
                  <Icon className="h-5 w-5" />
                  <span className={cn(isSidebarCollapsed ? 'sr-only' : '')}>{item.label}</span>
                </NavLink>
              );
            })}
          </nav>

          <div
            className={cn(
              'mt-auto border border-border bg-background',
              isSidebarCollapsed ? 'rounded-2xl p-2' : 'rounded-3xl p-4'
            )}
          >
            <div className={cn('flex items-center', isSidebarCollapsed ? 'justify-center' : 'gap-3')}>
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                {user?.avatarUrl ? (
                  <img src={user.avatarUrl} alt="" className="h-full w-full rounded-2xl object-cover" />
                ) : (
                  <span className="text-sm font-semibold">{user?.displayName?.charAt(0) || 'U'}</span>
                )}
              </div>
              <div className={cn('min-w-0 flex-1', isSidebarCollapsed ? 'hidden' : '')}>
                <p className="truncate font-semibold text-text">{user?.displayName || 'Player'}</p>
                <p className="truncate text-sm text-text-muted">{user?.phone}</p>
              </div>
            </div>

            <div className={cn('mt-4 space-y-2', isSidebarCollapsed ? 'mt-3' : '')}>
              <NavLink
                to="/report-a-problem"
                className={cn(
                  'flex items-center rounded-2xl text-sm font-medium text-text-muted transition-colors hover:bg-surface-elevated hover:text-text',
                  isSidebarCollapsed ? 'justify-center px-0 py-2.5' : 'gap-3 px-3 py-2.5'
                )}
                aria-label="Report a problem"
                title={isSidebarCollapsed ? 'Report a problem' : undefined}
              >
                <MessageCircleQuestion className="h-4 w-4" />
                <span className={cn(isSidebarCollapsed ? 'sr-only' : '')}>Report a problem</span>
              </NavLink>
              <button
                type="button"
                onClick={handleLogout}
                disabled={isLoggingOut}
                className={cn(
                  'flex w-full items-center rounded-2xl text-sm font-medium text-red-500 transition-colors hover:bg-red-500/10',
                  isSidebarCollapsed ? 'justify-center px-0 py-2.5' : 'gap-3 px-3 py-2.5'
                )}
                aria-label={isLoggingOut ? 'Signing out' : 'Sign out'}
                title={isSidebarCollapsed ? (isLoggingOut ? 'Signing out...' : 'Sign out') : undefined}
              >
                <LogOut className="h-4 w-4" />
                <span className={cn(isSidebarCollapsed ? 'sr-only' : '')}>{isLoggingOut ? 'Signing out...' : 'Sign out'}</span>
              </button>
            </div>
          </div>
        </div>
      </aside>

      <div className={cn('transition-[padding-left] duration-200', isSidebarCollapsed ? 'lg:pl-20' : 'lg:pl-72')}>
        <main
          data-layout="app-main"
          className={cn(
            'mr-auto min-h-screen w-full max-w-[1760px] tk-main pt-5 pb-24 md:px-5 lg:pl-2 lg:pr-8 lg:pt-2 lg:pb-10 2xl:pl-2 2xl:pr-10',
            isSquadsSection ? 'px-0' : 'px-4'
          )}
        >
          {isWorkspaceLoading ? (
            <div role="status" aria-label="Loading workspace" className="grid min-h-[40vh] place-items-center">
              <Loader2 aria-hidden="true" className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : <Outlet key={`${user?.id ?? 'none'}:${activeTenant?.tenant?.id ?? 'community'}`} />}
        </main>
      </div>

      <nav className="fixed bottom-0 left-0 right-0 z-40 border-t border-border bg-background/98 backdrop-blur lg:hidden">
        <div className="grid h-16 px-2" style={{ gridTemplateColumns: `repeat(${mobileNavItems.length}, minmax(0, 1fr))` }}>
          {mobileNavItems.map((item) => {
            const Icon = ICON_MAP[item.icon];
            const isActive = isNavItemActive(item) || item.to === '/organizer/competitions' && location.pathname.startsWith('/organizer/leagues');
            return (
              <NavLink
                key={item.to}
                to={item.to}
                aria-current={isActive ? 'page' : undefined}
                end={item.end ?? !item.to.includes('#')}
                className={() =>
                  cn(
                    'flex flex-col items-center justify-center gap-1 rounded-2xl text-[11px] font-medium transition-all',
                    isActive ? 'text-primary' : 'text-text-muted hover:text-text'
                  )
                }
              >
                <Icon className="h-5 w-5" />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </div>
      </nav>

      {showContextSwitcher ? (
        <WorkspaceSwitcher
          activeTenant={activeTenant}
          memberships={memberships}
          isSwitchingTenant={isSwitchingTenant}
          onClose={() => setShowContextSwitcher(false)}
          onSelectCommunity={handleCommunitySelect}
          onSelectTenant={handleTenantSelect}
        />
      ) : null}
    </div>
  );
}

function WorkspaceSwitcher({
  activeTenant,
  memberships,
  isSwitchingTenant,
  onClose,
  onSelectCommunity,
  onSelectTenant,
}: {
  activeTenant: ActiveTenantSummary | null;
  memberships: TenantMembershipSummary[];
  isSwitchingTenant: boolean;
  onClose: () => void;
  onSelectCommunity: () => void;
  onSelectTenant: (tenantId: number) => void;
}) {
  const activeTenantId = activeTenant?.tenant?.id ?? null;

  return (
    <WorkspaceDialog title="Switch TARK mode" onClose={onClose} busy={isSwitchingTenant} panelClassName="tk-mode-dialog" dismissOnBackdrop>
      <button type="button" disabled={isSwitchingTenant} onClick={onClose} className="tk-mode-close" aria-label="Close"><X aria-hidden="true" /></button>
      {isSwitchingTenant && <p role="status">Switching view…</p>}
        <div className="mt-6 space-y-3">
          <WorkspaceRow
            title="Play"
            subtitle="Squads, Buzz, TARK PROs, and open sports discovery"
            active={activeTenantId == null}
            disabled={activeTenantId == null || isSwitchingTenant}
            onClick={onSelectCommunity}
          />

          {memberships.map((membership) => {
            const tenant = membership.tenant;
            if (!tenant?.id) {
              return null;
            }

            return (
              <WorkspaceRow
                key={membership.membershipId}
                title={tenant.displayName}
                subtitle={
                  tenant.tenantArchetype === 'COACHING'
                    ? ['Coach Studio', membership.roleCode].filter(Boolean).join(' • ')
                    : [membership.roleCode, tenant.workTabLabel, tenant.directoryTabLabel].filter(Boolean).join(' • ')
                }
                active={activeTenantId === tenant.id}
                disabled={activeTenantId === tenant.id || isSwitchingTenant}
                onClick={() => onSelectTenant(tenant.id)}
              />
            );
          })}
        </div>
    </WorkspaceDialog>
  );
}


function WorkspaceRow({
  title,
  subtitle,
  active,
  disabled,
  onClick,
}: {
  title: string;
  subtitle: string;
  active: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex w-full items-center justify-between gap-4 rounded-2xl border px-4 py-4 text-left transition-all',
        active
          ? 'border-primary/40 bg-primary/10'
          : 'border-border bg-background hover:border-primary/30 hover:bg-surface-elevated',
        disabled && !active ? 'opacity-70' : ''
      )}
    >
      <div>
        <p className="font-semibold text-text">{title}</p>
        <p className="mt-1 text-sm text-text-muted">{subtitle}</p>
      </div>
      <div
        className={cn(
          'rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide',
          active ? 'bg-primary [color:var(--color-on-primary)]' : 'bg-surface text-text-subtle'
        )}
      >
        {active ? 'Active' : 'Open'}
      </div>
    </button>
  );
}
